import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Clip, Track } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";
import { findClip, patchClip, syncNestedInstance } from "./clip-helpers";

/**
 * The clip domain, the last of the eleven to come off the three hand-written
 * switches. Fourteen types, and they were not symmetric across those switches.
 *
 * clip/closeGapBefore, clip/merge, clip/restore and clip/rippleRestore had an
 * executor case and no validator case, and the validator's clip switch has no
 * default, so anything with a clip/ prefix and no case was accepted whatever it
 * carried. A merge with no originalClip reached apply and threw reading a
 * property of undefined, which execute reports as INVALID_PARAMS carrying the
 * raw TypeError text; a restore naming a track that is gone reported success,
 * changed nothing, and still took a history slot. Each gets a validate below
 * that checks what its own apply reads, and nothing more.
 *
 * clip/merge and clip/rippleRestore also had no inverse case, and neither gap
 * was reachable. Both exist only as the stored inverse of another action,
 * nothing outside packages/core dispatches either, undo applies a stored inverse
 * straight through applyAction, and redo replays the original action, so nothing
 * ever asks either type for an inverse. rippleRestore gets one anyway, a
 * clip/rippleDelete of the clip it puts back, mirroring the clip/restore to
 * clip/remove pairing the domain already had. merge keeps returning null:
 * undoing it means restoring the several clips it swallowed at once, no action
 * restores more than one clip, and a plausible wrong inverse is worse than none
 * now that ActionHistory.undo refuses an entry it cannot invert rather than
 * spending the press on it.
 *
 * The executor kept a compound instance's persisted timing in step with its
 * editable shell clip in a block AFTER its switch, gated on clip/move and
 * clip/trim. That placement is invisible from the case being moved, and it was
 * already dropped once in this migration without a test failing. Those two call
 * the shared syncNestedInstance; nothing else here calls it, because nothing
 * else here reached that block.
 *
 * clip/slip, clip/slide, clip/roll and clip/trimToPlayhead share this prefix and
 * are deliberately absent: they moved in an earlier pass and live in
 * clip-timing.ts. Registering a type twice leaves the winner to import order.
 *
 * The ids minted by clip/add and clip/split already came from crypto.randomUUID,
 * so nothing here needed the Date.now collision fix the effect and marker ids
 * got when those domains moved.
 */

function toResult(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

function findTrack(project: Project, trackId: string): Track | null {
  return project.timeline.tracks.find((t) => t.id === trackId) ?? null;
}

function mapClips(
  project: Project,
  fn: (clip: MutableClip) => MutableClip,
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
    ...track,
    clips: track.clips.map(fn),
  }));
}

interface ClipTarget {
  readonly clip: Clip | null;
  readonly errors: ValidationError[];
}

/**
 * The three id complaints the legacy switch raised, in the order it raised
 * them: the executor joins the messages into the one string it reports back, so
 * the order is part of what a caller sees.
 */
function clipTarget(clipId: unknown, project: Project): ClipTarget {
  if (!clipId || typeof clipId !== "string") {
    return {
      clip: null,
      errors: [
        {
          code: "INVALID_PARAMS",
          message: "Clip ID is required and must be a string",
          path: "params.clipId",
        },
      ],
    };
  }

  const clip = findClip(project, clipId);
  if (!clip) {
    return {
      clip: null,
      errors: [
        {
          code: "CLIP_NOT_FOUND",
          message: `Clip with ID ${clipId} not found`,
          path: "params.clipId",
        },
      ],
    };
  }

  const track = findTrack(project, clip.trackId);
  if (track?.locked) {
    return {
      clip,
      errors: [
        {
          code: "TRACK_LOCKED",
          message: `Track containing clip ${clipId} is locked`,
          path: "params.clipId",
        },
      ],
    };
  }

  return { clip, errors: [] };
}

/**
 * The two fields every handler that puts a clip back reads before it does
 * anything else: without an id the clip cannot be addressed again, and without
 * a trackId it has nowhere to land.
 */
function hasClipIdentity(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { id?: unknown; trackId?: unknown };
  return (
    typeof candidate.id === "string" && typeof candidate.trackId === "string"
  );
}

/**
 * clip/merge needs two more fields than that. Its apply builds the original's
 * span from startTime and duration and tests every clip on the track against
 * it, so an original carrying only an id and a trackId would pass an identity
 * check, compare against NaN bounds that nothing can fall inside, swallow
 * neither half of the split and leave a clip with no timing on the track.
 */
function hasClipSpan(value: unknown): boolean {
  if (!hasClipIdentity(value)) return false;
  const candidate = value as { startTime?: unknown; duration?: unknown };
  return (
    typeof candidate.startTime === "number" &&
    Number.isFinite(candidate.startTime) &&
    typeof candidate.duration === "number" &&
    Number.isFinite(candidate.duration)
  );
}

/**
 * Every field is spread first so nothing is silently dropped, then the mutable
 * nested structures are copied: the clip goes into a history entry that has to
 * outlive every later edit to the live project.
 */
function cloneClip(clip: Clip): Record<string, unknown> {
  return {
    ...(clip as unknown as Record<string, unknown>),
    effects: clip.effects.map((e) => ({ ...e, params: { ...e.params } })),
    audioEffects: clip.audioEffects.map((e) => ({
      ...e,
      params: { ...e.params },
    })),
    transform: { ...clip.transform },
    fade: clip.fade ? { ...clip.fade } : undefined,
    automation: clip.automation
      ? {
          volume: clip.automation.volume?.map((p) => ({ ...p })),
          pan: clip.automation.pan?.map((p) => ({ ...p })),
        }
      : undefined,
    keyframes: clip.keyframes.map((kf) => ({ ...kf })),
    emphasisAnimation: clip.emphasisAnimation
      ? { ...clip.emphasisAnimation }
      : undefined,
    metadata: clip.metadata ? { ...clip.metadata } : undefined,
  };
}

const clipAdd: ActionHandler = {
  type: "clip/add",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      trackId?: unknown;
      mediaId?: unknown;
      startTime?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.trackId || typeof params.trackId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Track ID is required and must be a string",
        path: "params.trackId",
      });
    } else {
      const track = findTrack(project, params.trackId);
      if (!track) {
        errors.push({
          code: "TRACK_NOT_FOUND",
          message: `Track with ID ${params.trackId} not found`,
          path: "params.trackId",
        });
      } else if (track.locked) {
        errors.push({
          code: "TRACK_LOCKED",
          message: `Track ${params.trackId} is locked`,
          path: "params.trackId",
        });
      }
    }

    if (!params.mediaId || typeof params.mediaId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Media ID is required and must be a string",
        path: "params.mediaId",
      });
    } else {
      const mediaId = params.mediaId;
      if (!project.mediaLibrary.items.some((item) => item.id === mediaId)) {
        errors.push({
          code: "MEDIA_NOT_FOUND",
          message: `Media with ID ${mediaId} not found`,
          path: "params.mediaId",
        });
      }
    }

    if (typeof params.startTime !== "number" || params.startTime < 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Start time must be a non-negative number",
        path: "params.startTime",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as {
      trackId: string;
      mediaId: string;
      startTime: number;
      duration?: number;
      inPoint?: number;
      outPoint?: number;
      volume?: number;
      effects?: unknown[];
      audioEffects?: unknown[];
      keyframes?: unknown[];
      transform?: Record<string, unknown>;
      fade?: { fadeIn: number; fadeOut: number };
      speed?: number;
      reversed?: boolean;
      audioTrackIndex?: number;
      sourceClip?: Clip;
    };
    const timeline = project.timeline as MutableTimeline;
    const track = timeline.tracks.find(
      (t: MutableTrack) => t.id === params.trackId,
    );
    if (!track) return;

    const mediaItem = project.mediaLibrary.items.find(
      (item) => item.id === params.mediaId,
    );
    // Images and graphics carry duration 0, so a media duration of 0 has to
    // fall through to the five second default rather than produce a clip with
    // no length.
    const clipDuration =
      params.duration ??
      (mediaItem?.metadata.duration && mediaItem.metadata.duration > 0
        ? mediaItem.metadata.duration
        : 5);
    const defaultTransform = {
      position: { x: 0, y: 0 },
      scale: { x: 1, y: 1 },
      rotation: 0,
      anchor: { x: 0.5, y: 0.5 },
      opacity: 1,
      fitMode: "contain" as const,
    };
    const newClip = params.sourceClip
      ? {
          ...structuredClone(params.sourceClip),
          id: crypto.randomUUID(),
          trackId: params.trackId,
          startTime: params.startTime,
        }
      : {
          id: crypto.randomUUID(),
          mediaId: params.mediaId,
          trackId: params.trackId,
          startTime: params.startTime,
          duration: clipDuration,
          inPoint: params.inPoint ?? 0,
          outPoint: params.outPoint ?? clipDuration,
          effects: (params.effects as never[]) ?? [],
          audioEffects: (params.audioEffects as never[]) ?? [],
          transform: params.transform
            ? { ...defaultTransform, ...params.transform }
            : defaultTransform,
          volume: params.volume ?? 1,
          keyframes: (params.keyframes as never[]) ?? [],
          ...(params.fade ? { fade: params.fade } : {}),
          ...(params.speed !== undefined ? { speed: params.speed } : {}),
          ...(params.reversed !== undefined
            ? { reversed: params.reversed }
            : {}),
          ...(params.audioTrackIndex !== undefined
            ? { audioTrackIndex: params.audioTrackIndex }
            : {}),
        };
    // The found track is written through rather than the track array rebuilt.
    // That is what the executor did, and which references come back new is what
    // the memoised timeline components re-render on, so it is not a free change.
    track.clips = [...track.clips, newClip];
    ctx.lastAddedIds.set("clip", newClip.id);
  },

  invert(action: Action): Action | null {
    // The id does not exist until apply has run, so the executor resolves this
    // marker against lastAddedIds while the new clip is still the latest one.
    return createInverseAction(action, "clip/remove", {
      clipId: "__LAST_ADDED__",
    });
  },
};

const clipRemove: ActionHandler = {
  type: "clip/remove",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown };
    return toResult(clipTarget(params.clipId, project).errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
      ...track,
      clips: track.clips.filter((c: MutableClip) => c.id !== params.clipId),
    }));
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    return createInverseAction(action, "clip/restore", {
      clip: cloneClip(clip),
    });
  },
};

/**
 * Minted by the inverse of a removal, and dispatched directly by the compound
 * clip flow in NestedSequenceSection, which is why the missing validator case
 * was reachable from the UI rather than only from history.
 *
 * Whether the named track still exists is deliberately not checked, though a
 * restore onto a track that is gone changes nothing and still reports success.
 * Undo replays an inverse through apply without validating it, so a stricter
 * rule would only ever refuse a caller replaying history, and that is a
 * different change from moving this code.
 */
const clipRestore: ActionHandler = {
  type: "clip/restore",

  validate(action: Action): ValidationResult {
    const clip = (action.params as { clip?: unknown }).clip;
    return toResult(
      hasClipIdentity(clip)
        ? []
        : [
            {
              code: "INVALID_PARAMS",
              message: "clip/restore requires a clip with an id and a trackId",
              path: "params.clip",
            },
          ],
    );
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clip: Clip };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
      track.id === params.clip.trackId
        ? { ...track, clips: [...track.clips, params.clip] }
        : track,
    );
  },

  invert(action: Action): Action | null {
    const params = action.params as { clip: Clip };
    return createInverseAction(action, "clip/remove", {
      clipId: params.clip.id,
    });
  },
};

const clipMove: ActionHandler = {
  type: "clip/move",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      clipId?: unknown;
      startTime?: unknown;
      trackId?: unknown;
    };
    const errors: ValidationError[] = [
      ...clipTarget(params.clipId, project).errors,
    ];

    if (typeof params.startTime !== "number" || params.startTime < 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Start time must be a non-negative number",
        path: "params.startTime",
      });
    }

    if (params.trackId !== undefined) {
      if (typeof params.trackId !== "string") {
        errors.push({
          code: "INVALID_PARAMS",
          message: "Track ID must be a string",
          path: "params.trackId",
        });
      } else {
        const targetTrack = findTrack(project, params.trackId);
        if (!targetTrack) {
          errors.push({
            code: "TRACK_NOT_FOUND",
            message: `Target track with ID ${params.trackId} not found`,
            path: "params.trackId",
          });
        } else if (targetTrack.locked) {
          errors.push({
            code: "TRACK_LOCKED",
            message: `Target track ${params.trackId} is locked`,
            path: "params.trackId",
          });
        }
      }
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      startTime: number;
      trackId?: string;
    };
    const timeline = project.timeline as MutableTimeline;
    const clip = findClip(project, params.clipId);
    if (clip) {
      timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
        ...track,
        clips: track.clips.filter((c: MutableClip) => c.id !== params.clipId),
      }));
      const targetTrackId = params.trackId || clip.trackId;
      timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
        track.id === targetTrackId
          ? {
              ...track,
              clips: [
                ...track.clips,
                {
                  ...clip,
                  startTime: params.startTime,
                  trackId: targetTrackId,
                },
              ],
            }
          : track,
      );
    }
    // Outside the guard, where the executor's post-switch block also sat: it ran
    // whether or not the case had found anything, and it no-ops for a clip that
    // is not a compound shell.
    syncNestedInstance(project, params.clipId);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    return createInverseAction(action, "clip/move", {
      clipId: params.clipId,
      startTime: clip.startTime,
      trackId: clip.trackId,
    });
  },
};

const clipTrim: ActionHandler = {
  type: "clip/trim",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      clipId?: unknown;
      inPoint?: unknown;
      outPoint?: unknown;
    };
    const errors: ValidationError[] = [
      ...clipTarget(params.clipId, project).errors,
    ];

    if (
      params.inPoint !== undefined &&
      (typeof params.inPoint !== "number" || params.inPoint < 0)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "In-point must be a non-negative number",
        path: "params.inPoint",
      });
    }

    if (
      params.outPoint !== undefined &&
      (typeof params.outPoint !== "number" || params.outPoint < 0)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Out-point must be a non-negative number",
        path: "params.outPoint",
      });
    }

    if (
      params.inPoint !== undefined &&
      params.outPoint !== undefined &&
      (params.outPoint as number) <= (params.inPoint as number)
    ) {
      errors.push({
        code: "INVALID_TIME_RANGE",
        message: "Out-point must be greater than in-point",
        path: "params",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clipId: string;
      inPoint?: number;
      outPoint?: number;
    };
    mapClips(project, (clip: MutableClip) => {
      if (clip.id !== params.clipId) return clip;

      // Duration always derives from the EFFECTIVE window. Computing it per
      // param read the stale clip.inPoint when both ends were given, and the
      // inverse of a trim always gives both, so every undo of an in-point trim
      // restored the wrong duration.
      const nextInPoint = params.inPoint ?? clip.inPoint;
      const nextOutPoint = params.outPoint ?? clip.outPoint;
      const newDuration = nextOutPoint - nextInPoint;
      const updates: Partial<MutableClip> = {
        inPoint: nextInPoint,
        outPoint: nextOutPoint,
        duration: newDuration,
      };
      // Exit keyframes anchor to the end of the clip, so a trim has to carry
      // them with the new end. Without this, the action (and its inverse, which
      // is also a trim) left exit animations stranded at the old end time after
      // every undo or redo.
      if (
        newDuration !== clip.duration &&
        clip.keyframes.some((kf) => kf.id.startsWith("kf-exit-"))
      ) {
        updates.keyframes = clip.keyframes.map((kf) =>
          kf.id.startsWith("kf-exit-")
            ? { ...kf, time: newDuration + (kf.time - clip.duration) }
            : kf,
        );
      }
      return { ...clip, ...updates };
    });
    syncNestedInstance(project, params.clipId);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    return createInverseAction(action, "clip/trim", {
      clipId: params.clipId,
      inPoint: clip.inPoint,
      outPoint: clip.outPoint,
    });
  },
};

const clipSplit: ActionHandler = {
  type: "clip/split",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown; time?: unknown };
    const target = clipTarget(params.clipId, project);
    // A clip that cannot be addressed stops the check here, so the caller is
    // never handed a bounds message built from coordinates that do not exist.
    if (!target.clip) return toResult(target.errors);

    const errors: ValidationError[] = [...target.errors];
    const clip = target.clip;
    if (typeof params.time === "number") {
      if (
        params.time <= clip.startTime ||
        params.time >= clip.startTime + clip.duration
      ) {
        errors.push({
          code: "OUT_OF_BOUNDS",
          message: `Split time must be within clip bounds (${
            clip.startTime
          } to ${clip.startTime + clip.duration})`,
          path: "params.time",
        });
      }
    } else {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Split time is required and must be a number",
        path: "params.time",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as { clipId: string; time: number };
    const clip = findClip(project, params.clipId);
    if (!clip) return;

    const splitTime = params.time;
    const splitOffset = splitTime - clip.startTime;

    const clip1 = {
      ...clip,
      duration: splitOffset,
      outPoint: clip.inPoint + splitOffset,
    };

    const clip2 = {
      ...clip,
      id: crypto.randomUUID(),
      startTime: splitTime,
      duration: clip.duration - splitOffset,
      inPoint: clip.inPoint + splitOffset,
    };

    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
      ...track,
      clips: track.clips.flatMap((c: MutableClip) =>
        c.id === params.clipId ? [clip1, clip2] : [c],
      ),
    }));

    ctx.lastAddedIds.set("clip", clip2.id);
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    // Undoing a split means putting the whole clip back, so the inverse carries
    // the clip as it was rather than a second half to delete.
    return createInverseAction(action, "clip/merge", {
      clipId: params.clipId,
      originalClip: cloneClip(clip),
    });
  },
};

/**
 * Only ever minted as the inverse of a split, and nothing outside packages/core
 * dispatches it, which is how it went without a validator case for so long.
 */
const clipMerge: ActionHandler = {
  type: "clip/merge",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      clipId?: unknown;
      originalClip?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.clipId || typeof params.clipId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Clip ID is required and must be a string",
        path: "params.clipId",
      });
    }

    if (!hasClipSpan(params.originalClip)) {
      errors.push({
        code: "INVALID_PARAMS",
        message:
          "clip/merge requires the original clip it puts back, with an id, a trackId and its span",
        path: "params.originalClip",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string; originalClip: Clip };
    const timeline = project.timeline as MutableTimeline;
    const origStart = params.originalClip.startTime;
    const origEnd = origStart + params.originalClip.duration;
    const origMediaId = params.originalClip.mediaId;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => {
      if (track.id === params.originalClip.trackId) {
        const filteredClips = track.clips.filter((c: MutableClip) => {
          if (c.id === params.clipId) return false;
          if (c.mediaId === origMediaId) {
            const clipEnd = c.startTime + c.duration;
            if (c.startTime >= origStart && clipEnd <= origEnd) {
              return false;
            }
          }
          return true;
        });
        return { ...track, clips: [...filteredClips, params.originalClip] };
      }
      return track;
    });
  },

  invert(): Action | null {
    // A merge swallows every clip sitting inside the original's span, and no
    // action restores more than one clip, so there is nothing to return that
    // would put them all back. Null is what the generator returned before, and
    // undo refuses a null inverse rather than acting on a wrong one.
    return null;
  },
};

const clipRippleDelete: ActionHandler = {
  type: "clip/rippleDelete",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown };
    return toResult(clipTarget(params.clipId, project).errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string };
    const timeline = project.timeline as MutableTimeline;
    const clip = findClip(project, params.clipId);
    if (!clip) return;

    const deletedDuration = clip.duration;
    const deletedStartTime = clip.startTime;

    timeline.tracks = timeline.tracks.map((track: MutableTrack) => {
      if (track.id === clip.trackId) {
        return {
          ...track,
          clips: track.clips
            .filter((c: MutableClip) => c.id !== params.clipId)
            .map((c: MutableClip) =>
              c.startTime > deletedStartTime
                ? { ...c, startTime: c.startTime - deletedDuration }
                : c,
            ),
        };
      }
      return track;
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    const track = findTrack(projectBefore, clip.trackId);
    const affectedClips =
      track?.clips
        .filter((c) => c.startTime > clip.startTime)
        .map((c) => ({ id: c.id, originalStartTime: c.startTime })) || [];

    return createInverseAction(action, "clip/rippleRestore", {
      clip: cloneClip(clip),
      affectedClips,
    });
  },
};

const clipRippleRestore: ActionHandler = {
  type: "clip/rippleRestore",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      clip?: unknown;
      affectedClips?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!hasClipIdentity(params.clip)) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "clip/rippleRestore requires a clip with an id and a trackId",
        path: "params.clip",
      });
    }

    // apply calls find on this for every clip on the track, so a missing one is
    // a throw rather than a no-op.
    if (!Array.isArray(params.affectedClips)) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "clip/rippleRestore requires an affectedClips array",
        path: "params.affectedClips",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      clip: Clip;
      affectedClips: Array<{ id: string; originalStartTime: number }>;
    };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => {
      if (track.id === params.clip.trackId) {
        const restoredClips = track.clips.map((c: MutableClip) => {
          const affected = params.affectedClips.find((ac) => ac.id === c.id);
          return affected ? { ...c, startTime: affected.originalStartTime } : c;
        });
        return { ...track, clips: [...restoredClips, params.clip] };
      }
      return track;
    });
  },

  invert(action: Action): Action | null {
    // A ripple delete of the clip this puts back is its exact opposite:
    // rippleDelete shifts everything starting strictly after that clip left by
    // its duration, which is the arrangement the affectedClips list carried here
    // was built from. Reachable only from a direct dispatch, since undo replays
    // a stored inverse rather than inverting it again.
    const params = action.params as { clip: Clip };
    return createInverseAction(action, "clip/rippleDelete", {
      clipId: params.clip.id,
    });
  },
};

const clipCloseGapBefore: ActionHandler = {
  type: "clip/closeGapBefore",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { clipId?: unknown };
    const target = clipTarget(params.clipId, project);
    // The locked-track error is dropped rather than reported. This type never
    // carried that check, so adding one would refuse an edit that goes through
    // today, which is a product decision rather than part of moving the code.
    return toResult(target.clip ? [] : target.errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { clipId: string };
    const timeline = project.timeline as MutableTimeline;
    const clip = findClip(project, params.clipId);
    if (!clip) return;

    const track = timeline.tracks.find((t) => t.id === clip.trackId);
    if (!track) return;

    // Timeline order, not array order: the clips on a track are not held
    // sorted, so the clip before this one is not the one before it in the array.
    const sorted = [...track.clips].sort((a, b) => a.startTime - b.startTime);
    const idx = sorted.findIndex((c) => c.id === clip.id);
    if (idx < 0) return;

    const prev = idx > 0 ? sorted[idx - 1] : null;
    const target = prev ? prev.startTime + prev.duration : 0;
    const gap = clip.startTime - target;
    if (gap <= 0) return;

    // Everything from this clip rightwards moves by the same amount, so the
    // cuts after the gap keep their spacing.
    const shiftIds = new Set(sorted.slice(idx).map((c) => c.id));
    timeline.tracks = timeline.tracks.map((t: MutableTrack) => {
      if (t.id !== track.id) return t;
      return {
        ...t,
        clips: t.clips.map((c: MutableClip) =>
          shiftIds.has(c.id) ? { ...c, startTime: c.startTime - gap } : c,
        ),
      };
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { clipId: string };
    const clip = findClip(projectBefore, params.clipId);
    if (!clip) return null;

    const track = findTrack(projectBefore, clip.trackId);
    if (!track) return null;

    const positions = track.clips
      .filter((c) => c.startTime >= clip.startTime)
      .map((c) => ({ clipId: c.id, startTime: c.startTime }));

    return createInverseAction(action, "track/restorePositions", {
      trackId: track.id,
      positions,
    });
  },
};

interface ClipPropertySpec {
  readonly type: string;
  readonly paramKey: string;
  readonly field: keyof MutableClip;
  readonly priorValue: (clip: Clip) => unknown;
}

/**
 * Not makeClipFieldHandler from clip-helpers, which reports a clip that is not
 * there with a different code and message, runs no locked-track check, and
 * inverts to whatever the field held. These four need the defaults below: a
 * clip that never had a blend mode set has to undo back to "normal", not to
 * undefined.
 */
function makeClipPropertyHandler(spec: ClipPropertySpec): ActionHandler {
  return {
    type: spec.type,

    validate(action: Action, project: Project): ValidationResult {
      const params = action.params as { clipId?: unknown };
      return toResult(clipTarget(params.clipId, project).errors);
    },

    apply(action: Action, project: Project): void {
      const params = action.params as Record<string, unknown>;
      patchClip(project, params.clipId as string, {
        [spec.field]: params[spec.paramKey],
      } as Partial<MutableClip>);
    },

    invert(action: Action, projectBefore: Project): Action | null {
      const clipId = (action.params as { clipId: string }).clipId;
      const prior = findClip(projectBefore, clipId);
      if (!prior) return null;

      return createInverseAction(action, spec.type, {
        clipId,
        [spec.paramKey]: spec.priorValue(prior),
      });
    },
  };
}

for (const handler of [
  clipAdd,
  clipRemove,
  clipRestore,
  clipMove,
  clipTrim,
  clipSplit,
  clipMerge,
  clipRippleDelete,
  clipRippleRestore,
  clipCloseGapBefore,
  makeClipPropertyHandler({
    type: "clip/setBlendMode",
    paramKey: "blendMode",
    field: "blendMode",
    priorValue: (clip) => clip.blendMode ?? "normal",
  }),
  makeClipPropertyHandler({
    type: "clip/setBlendOpacity",
    paramKey: "opacity",
    field: "blendOpacity",
    priorValue: (clip) => clip.blendOpacity ?? 100,
  }),
  makeClipPropertyHandler({
    type: "clip/setEmphasisAnimation",
    paramKey: "emphasisAnimation",
    field: "emphasisAnimation",
    priorValue: (clip) =>
      clip.emphasisAnimation ? { ...clip.emphasisAnimation } : undefined,
  }),
  makeClipPropertyHandler({
    type: "clip/setColorGrading",
    paramKey: "colorGrading",
    field: "colorGrading",
    priorValue: (clip) =>
      clip.colorGrading ? { ...clip.colorGrading } : undefined,
  }),
]) {
  registerActionHandler(handler);
}
